import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Mail,
  Users,
  ListOrdered,
  FileEdit,
  Filter,
  Zap
} from 'lucide-react';

import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { PageTitle } from '../shared';
import {
  rememberPageValue,
} from '../navigation/adminPlaceMemory';
import { getAdminUser } from '../navigation/sectionAccess';

import NewsletterListsTab from './NewsletterListsTab';
import NewsletterRecipientsTab from './NewsletterRecipientsTab';
import NewsletterEditorTab from './NewsletterEditorTab';
import NewsletterSegmentsTab from './NewsletterSegmentsTab';
import NewsletterAutomationsTab from './NewsletterAutomationsTab';

const TABS = [
  { id: 'lists', label: 'Email Lists', icon: ListOrdered, description: 'Manage subscriber lists' },
  { id: 'recipients', label: 'Recipients', icon: Users, description: 'Manage newsletter subscribers' },
  { id: 'segments', label: 'Segments', icon: Filter, description: 'Create dynamic member segments' },
  { id: 'automations', label: 'Automations', icon: Zap, description: 'Set up automated email sequences' },
  { id: 'newsletters', label: 'Newsletters', icon: FileEdit, description: 'Create and send newsletters' },
];

export default function NewsletterManager() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tabIds = TABS.map((tab) => tab.id);
  const activeTab = tabIds.includes(requestedTab)
    ? requestedTab
    : 'lists';
  const [message, setMessage] = useState(null);

  const selectTab = (tabId) => {
    rememberPageValue('newsletter', tabId, tabIds, getAdminUser());
    setSearchParams({ tab: tabId });
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'lists':
        return <NewsletterListsTab showMessage={showMessage} />;
      case 'recipients':
        return <NewsletterRecipientsTab showMessage={showMessage} />;
      case 'segments':
        return <NewsletterSegmentsTab showMessage={showMessage} />;
      case 'automations':
        return <NewsletterAutomationsTab showMessage={showMessage} />;
      case 'newsletters':
        return (
          <NewsletterEditorTab
            showMessage={showMessage}
            startComposing={searchParams.get('compose') === '1'}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <PageTitle
        title="Newsletter Manager"
        description="Create and send newsletters to your subscribers"
        icon={<Mail className="h-6 w-6" />}
      />

      {/* Status Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tab Navigation - icon-only on mobile, icon + text on sm+ */}
      <TooltipProvider>
        <div className="flex gap-1 sm:gap-2">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <Tooltip key={tab.id}>
                <TooltipTrigger asChild>
                  <Button
                    variant={activeTab === tab.id ? 'adminPrimary' : 'ghost'}
                    onClick={() => selectTab(tab.id)}
                    className="flex items-center px-2.5 sm:px-3 sm:gap-2"
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="sm:hidden">
                  {tab.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      {/* Tab Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {React.createElement(TABS.find(t => t.id === activeTab)?.icon || Mail, { className: "h-5 w-5" })}
            {TABS.find(t => t.id === activeTab)?.label}
          </CardTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {TABS.find(t => t.id === activeTab)?.description}
          </p>
        </CardHeader>
        <CardContent>
          {renderActiveTab()}
        </CardContent>
      </Card>
    </div>
  );
}
